"use client";

import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  type MediaItem,
  mediaItemSrc,
  mediaPreviewLooksImage,
  mediaPreviewLooksVideo,
} from "@/lib/mediaListHelpers";

export type MediaCarouselState = {
  items: MediaItem[];
  /** Index within `items`; clamped when the overlay opens */
  initialIndex: number;
};

export function MediaCarouselOverlay({
  open,
  onOpenChange,
  carousel,
  captionEditable = false,
  onItemsChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carousel: MediaCarouselState | null;
  captionEditable?: boolean;
  onItemsChange?: ({ items }: { items: MediaItem[] }) => void;
}) {
  const items = carousel?.items ?? [];
  const [index, setIndex] = useState(0);
  const [draftCaption, setDraftCaption] = useState("");
  const itemsRef = useRef(items);
  const indexRef = useRef(index);
  const draftCaptionRef = useRef(draftCaption);
  itemsRef.current = items;
  indexRef.current = index;
  draftCaptionRef.current = draftCaption;
  const dialogOpen = open && items.length > 0;

  const flushCaptionDraft = useCallback(() => {
    if (!captionEditable || !onItemsChange) return;
    const list = itemsRef.current;
    const idx = indexRef.current;
    if (!list[idx]) return;
    const trimmed = draftCaptionRef.current.trim();
    const prev = list[idx]?.caption ?? "";
    if (trimmed === prev) return;
    const nextItems = list.map((it, i) => {
      if (i !== idx) return it;
      return { ...it, caption: trimmed };
    });
    onItemsChange({ items: nextItems });
  }, [captionEditable, onItemsChange]);

  useEffect(() => {
    if (!dialogOpen || items.length === 0) return;
    const clamped =
      carousel?.initialIndex != null
        ? Math.min(
            Math.max(0, carousel.initialIndex),
            Math.max(0, items.length - 1),
          )
        : 0;
    setIndex(clamped);
  }, [dialogOpen, carousel?.initialIndex, items.length]);

  const active = items[index];

  useEffect(() => {
    if (!dialogOpen) return;
    setDraftCaption(active?.caption ?? "");
  }, [dialogOpen, index, active?.value, active?.caption]);

  const go = useCallback(
    ({ direction }: { direction: number }) => {
      if (items.length === 0) return;
      setIndex((i) => {
        const n = items.length;
        return (i + direction + n) % n;
      });
    },
    [items.length],
  );

  useEffect(() => {
    if (!dialogOpen || items.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        go({ direction: 1 });
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go({ direction: -1 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialogOpen, items.length, go]);

  const count = items.length;

  return (
    <DialogPrimitive.Root
      open={dialogOpen}
      onOpenChange={(next) => {
        if (!next) {
          flushCaptionDraft();
          onOpenChange(false);
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[100] bg-black/90 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-0 z-[101] flex flex-col outline-none",
          )}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogPrimitive.Title className="sr-only">
            {count > 1
              ? `Media preview, ${index + 1} of ${count}`
              : "Media preview"}
          </DialogPrimitive.Title>
          <div className="absolute top-4 right-4 left-4 z-[102] flex items-center justify-between gap-4">
            {count > 1 ? (
              <p className="min-w-0 truncate text-sm text-white/90 drop-shadow-md">
                <span className="text-white/70">
                  {index + 1} / {count}
                </span>
              </p>
            ) : null}
            <div className="flex shrink-0 items-center gap-2">
              {active?.value ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon-sm"
                  className="bg-white/15 text-white hover:bg-white/25"
                  asChild
                >
                  <a
                    href={mediaItemSrc(active)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open in new tab"
                  >
                    <ExternalLink className="size-4" aria-hidden />
                    <span className="sr-only">Open in new tab</span>
                  </a>
                </Button>
              ) : null}
              <DialogPrimitive.Close asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="bg-white/15 text-white hover:bg-white/25"
                >
                  Close
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>

          {count > 1 ? (
            <>
              <button
                type="button"
                className={cn(
                  "absolute left-2 top-1/2 z-[102] flex size-12 -translate-y-1/2 items-center justify-center rounded-full",
                  "bg-white/15 text-white shadow-md backdrop-blur-sm hover:bg-white/25",
                  "focus-visible:ring-[3px] focus-visible:ring-white/60 focus-visible:outline-none",
                )}
                aria-label="Previous media"
                onClick={() => go({ direction: -1 })}
              >
                <ChevronLeft className="size-8" aria-hidden />
              </button>
              <button
                type="button"
                className={cn(
                  "absolute right-2 top-1/2 z-[102] flex size-12 -translate-y-1/2 items-center justify-center rounded-full",
                  "bg-white/15 text-white shadow-md backdrop-blur-sm hover:bg-white/25",
                  "focus-visible:ring-[3px] focus-visible:ring-white/60 focus-visible:outline-none",
                )}
                aria-label="Next media"
                onClick={() => go({ direction: 1 })}
              >
                <ChevronRight className="size-8" aria-hidden />
              </button>
            </>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-auto px-14 pt-16 pb-8">
            {active ? (
              <div className="flex flex-col items-center gap-3">
                <CarouselSlide item={active} />
                {captionEditable && onItemsChange ? (
                  <Textarea
                    value={draftCaption}
                    onChange={(e) => setDraftCaption(e.target.value)}
                    onBlur={() => flushCaptionDraft()}
                    placeholder="Caption"
                    aria-label="Media caption"
                    rows={3}
                    className="max-h-32 max-w-[min(96vw,calc(100vw-8rem))] min-h-14 border-white/25 bg-black/35 text-white shadow-md placeholder:text-white/45 focus-visible:border-white/55 focus-visible:ring-white/30"
                  />
                ) : active.caption ? (
                  <p className="max-w-[min(96vw,calc(100vw-8rem))] text-center text-sm text-white/85 drop-shadow-md">
                    {active.caption}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function CarouselSlide({ item }: { item: MediaItem }) {
  const src = mediaItemSrc(item);
  const imgAlt =
    item.caption.trim() ? item.caption : item.value;
  if (mediaPreviewLooksVideo(item)) {
    return (
      <video
        key={item.value}
        src={src}
        className="max-h-[min(78vh,calc(100vh-10rem))] max-w-[min(96vw,calc(100vw-8rem))] rounded-md shadow-2xl"
        controls
        playsInline
        autoPlay={false}
        preload="metadata"
      />
    );
  }
  if (mediaPreviewLooksImage(item)) {
    return (
      <img
        key={item.value}
        src={src}
        alt={imgAlt}
        className="max-h-[min(78vh,calc(100vh-10rem))] max-w-[min(96vw,calc(100vw-8rem))] rounded-md object-contain shadow-2xl"
      />
    );
  }
  return (
    <div className="bg-background/95 text-foreground flex max-h-[min(78vh,calc(100vh-10rem))] max-w-[min(96vw,calc(100vw-8rem))] flex-col items-center gap-4 rounded-lg border p-10 shadow-2xl">
      <p className="text-center text-sm break-all">{item.value}</p>
      <Button type="button" variant="outline" size="sm" asChild>
        <a href={src} target="_blank" rel="noreferrer">
          Open file
        </a>
      </Button>
    </div>
  );
}
